import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { useTerm } from "@/lib/terminology.jsx";
import { userTitle } from "@/lib/userDisplay";
import {
  Kanban, ShieldCheck, Bot, FileText,
  UserCog, LogOut, Building2, Users, Server,
  ScrollText, LayoutDashboard, Bell, Settings as SettingsIcon,
  ListTodo, UserCircle, FileSpreadsheet, ChevronDown, Inbox, KeyRound,
} from "lucide-react";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// ---- Role-based nav (spec 0.9) --------------------------------------------
// v5.7 Addition 4: Coming-soon items (Portfolio, Fund Management, Monitoring)
// are removed entirely.
const NAV_BY_ROLE = {
  superadmin: [
    { to: "/superadmin", label: "Dashboard", icon: LayoutDashboard, active: true, testid: "nav-sa-dashboard" },
    { to: "/superadmin", label: "Tenant Management", icon: Building2, active: true, testid: "nav-sa-tenants" },
    { to: "/superadmin/users", label: "User Management", icon: Users, active: false, testid: "nav-sa-users" },
    { to: "/superadmin/pipeline", label: "Pipeline", icon: Kanban, active: false, testid: "nav-sa-pipeline" },
    { to: "/superadmin/gates", label: "Gate Framework", icon: ShieldCheck, active: false, testid: "nav-sa-gates" },
    { to: "/superadmin/agents", label: "Agents Library", icon: Bot, active: false, testid: "nav-sa-agents" },
    { to: "/superadmin/reporting", label: "Reporting", icon: FileText, active: false, testid: "nav-sa-reporting" },
    { to: "/superadmin/system", label: "System Config", icon: Server, active: false, testid: "nav-sa-system" },
    { to: "/superadmin/audit", label: "Audit Log", icon: ScrollText, active: false, testid: "nav-sa-audit" },
  ],
  vc_admin: [
    { to: "/pipeline", label: "Pipeline", icon: Kanban, active: true, testid: "nav-pipeline" },
    { to: "/approvals", label: "New Deal Approvals", icon: Inbox, active: true, testid: "nav-approvals", badgeKey: "approvals" },
    { to: "/tasks", label: "Tasks", icon: ListTodo, active: true, testid: "nav-tasks" },
    { to: "/notifications", label: "Notifications", icon: Bell, active: true, testid: "nav-notifications" },
    { to: "/gates", label: "Gates", icon: ShieldCheck, active: true, testid: "nav-gates" },
    { to: "/autogates", label: "AutoGates", icon: Bot, active: true, testid: "nav-autogates" },
    { to: "/agents", label: "Agents Library", icon: Bot, active: true, testid: "nav-agents" },
    { to: "/reports/pipeline", label: "Reporting", icon: FileText, active: true, testid: "nav-reports" },
    { to: "/users", label: "User Management", icon: UserCog, active: true, testid: "nav-users" },
    { to: "/bulk", label: "Bulk Upload/Download", icon: FileSpreadsheet, active: true, testid: "nav-bulk" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, active: true, testid: "nav-settings" },
  ],
  it_admin: null, // duplicated from vc_admin below
  senior_vc: [
    { to: "/pipeline", label: "Pipeline", icon: Kanban, active: true, testid: "nav-pipeline" },
    { to: "/approvals", label: "New Deal Approvals", icon: Inbox, active: true, testid: "nav-approvals", badgeKey: "approvals" },
    { to: "/tasks", label: "Tasks", icon: ListTodo, active: true, testid: "nav-tasks" },
    { to: "/notifications", label: "Notifications", icon: Bell, active: true, testid: "nav-notifications" },
    { to: "/gates", label: "Gates", icon: ShieldCheck, active: true, testid: "nav-gates" },
    { to: "/autogates", label: "AutoGates", icon: Bot, active: true, testid: "nav-autogates" },
    { to: "/agents", label: "Agents Library", icon: Bot, active: true, testid: "nav-agents" },
    { to: "/reports/pipeline", label: "Reporting", icon: FileText, active: true, testid: "nav-reports" },
    { to: "/bulk", label: "Bulk Upload/Download", icon: FileSpreadsheet, active: true, testid: "nav-bulk" },
  ],
  junior_vc: [
    { to: "/pipeline", label: "Pipeline", icon: Kanban, active: true, testid: "nav-pipeline" },
    { to: "/approvals", label: "New Deal Approvals", icon: Inbox, active: true, testid: "nav-approvals", badgeKey: "approvals" },
    { to: "/tasks", label: "Tasks", icon: ListTodo, active: true, testid: "nav-tasks" },
    { to: "/notifications", label: "Notifications", icon: Bell, active: true, testid: "nav-notifications" },
    { to: "/gates", label: "Gates", icon: ShieldCheck, active: true, testid: "nav-gates" },
    { to: "/autogates", label: "AutoGates", icon: Bot, active: true, testid: "nav-autogates" },
    { to: "/agents", label: "Agents Library", icon: Bot, active: true, testid: "nav-agents" },
    { to: "/reports/pipeline", label: "Reporting", icon: FileText, active: true, testid: "nav-reports" },
    { to: "/bulk", label: "Bulk Download", icon: FileSpreadsheet, active: true, testid: "nav-bulk" },
  ],
};
NAV_BY_ROLE.it_admin = NAV_BY_ROLE.vc_admin;

// Translate a nav label word-by-word so multi-word labels like "Gate Framework"
// or "Agents Library" get partial rewrites (e.g. "Deals Library" if Agents → Deals).
function translateLabel(label, t) {
  if (!label) return label;
  return label.split(/(\s+)/).map((tok) => (/^\s+$/.test(tok) ? tok : t(tok))).join("");
}

// Compute the routes each role is allowed to visit (spec 0.9 route protection)
export function allowedRoutesForRole(role) {
  const nav = NAV_BY_ROLE[role] || [];
  const routes = new Set(nav.map((i) => i.to));
  if (role !== "superadmin") {
    routes.add("/deals");
    routes.add("/gates/new");
    routes.add("/gates/:gateId");
    routes.add("/agents/new");
    routes.add("/agents/:agentId");
    routes.add("/reports");
  }
  return routes;
}

// Default landing (spec 0.9)
export function defaultLandingForRole(role) {
  if (role === "superadmin") return "/superadmin";
  return "/pipeline";
}

// v5.7 Addition 1: Global Pipeline Switcher
function PipelineSwitcher() {
  const { pipelines, pipelineId, setPipelineId, multiEnabled } = usePipelineCtx();
  const t = useTerm();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!multiEnabled) return null;
  if (!pipelines || pipelines.length < 2) return null;

  const current = pipelines.find((p) => p.pipeline_id === pipelineId) || pipelines[0];

  return (
    <div className="px-4 py-3 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }} ref={ref}>
      <div className="text-[10px] uppercase tracking-widest mb-1.5"
        style={{ color: "rgba(255,255,255,0.45)" }}>
        Active {t("Pipeline")}
      </div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm font-medium transition"
        style={{ background: "rgba(255,255,255,0.08)", color: "white" }}
        data-testid="pipeline-switcher-button"
      >
        <span className="truncate">{current?.pipeline_name || "Select…"}</span>
        <ChevronDown size={14} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="mt-1 rounded-md overflow-hidden shadow-lg"
          style={{ background: "var(--cw-primary-dark)", border: "1px solid rgba(255,255,255,0.12)" }}
          data-testid="pipeline-switcher-menu"
        >
          {pipelines.map((p) => (
            <button
              key={p.pipeline_id}
              onClick={() => { setPipelineId(p.pipeline_id); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-white/10 transition"
              style={{ color: p.pipeline_id === current?.pipeline_id ? "var(--cw-secondary)" : "white" }}
              data-testid={`pipeline-switcher-option-${p.pipeline_id}`}
            >
              {p.pipeline_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, tenant, logout } = useAuth();
  const t = useTerm();
  const navigate = useNavigate();
  const nav = NAV_BY_ROLE[user?.role] || [];
  const isSuper = user?.role === "superadmin";
  const [notifCount, setNotifCount] = useState(0);
  const [approvalsCount, setApprovalsCount] = useState(0);
  const [showPwd, setShowPwd] = useState(false);

  // Apply tenant branding via CSS vars (v5.5 Addition 8)
  useEffect(() => {
    if (!tenant) return;
    const root = document.documentElement;
    if (tenant.primary_color) root.style.setProperty("--cw-primary", tenant.primary_color);
    if (tenant.secondary_color) root.style.setProperty("--cw-secondary", tenant.secondary_color);
  }, [tenant?.primary_color, tenant?.secondary_color]);

  // Poll notifications count
  useEffect(() => {
    if (!user || isSuper) return;
    const fetch = () => api.get("/notifications/count")
      .then(({ data }) => setNotifCount(data.count || 0))
      .catch(() => {});
    fetch();
    const t = setInterval(fetch, 30000);
    return () => clearInterval(t);
  }, [user, isSuper]);

  // Poll approvals pending count
  useEffect(() => {
    if (!user || isSuper) return;
    const fetch = () => api.get("/mailbox/settings")
      .then(({ data }) => setApprovalsCount(data.pending_count || 0))
      .catch(() => {});
    fetch();
    const t = setInterval(fetch, 30000);
    return () => clearInterval(t);
  }, [user, isSuper]);

  const brandLogo = tenant?.logo_url;
  const brandOrgName = tenant?.org_name || tenant?.tenant_name;
  const brandTagline = tenant?.tagline;

  return (
    <div className="flex min-h-screen">
      <aside
        className="text-white flex flex-col shrink-0"
        style={{ width: 240, background: "var(--cw-primary-dark)" }}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate(defaultLandingForRole(user?.role))}
            data-testid="brand-logo"
          >
            {brandLogo ? (
              <img src={brandLogo} alt="logo" className="h-8 w-auto object-contain" />
            ) : (
              <div className="text-[22px] font-bold tracking-tight">
                Compete<span style={{ color: "var(--cw-secondary)" }}>Wiser</span>
              </div>
            )}
          </div>
          <div className="cw-caption mt-1" style={{ color: "rgba(255,255,255,0.55)" }}>
            {isSuper ? "Superadmin Console" : (brandOrgName || "VC OS")}
          </div>
          {!isSuper && brandTagline && (
            <div className="text-[10px] mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
              {brandTagline}
            </div>
          )}
        </div>
        {!isSuper && <PipelineSwitcher />}
        <nav className="flex-1 py-4 space-y-0.5 overflow-y-auto">
          {nav.map((item) => (
            <NavLink
              key={item.testid}
              to={item.to}
              className={({ isActive }) => `cw-nav-item ${isActive ? "active" : ""}`}
              data-testid={item.testid}
              end
            >
              <item.icon size={16} />
              <span className="flex-1">{translateLabel(item.label, t)}</span>
              {item.testid === "nav-notifications" && notifCount > 0 && (
                <span
                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                  style={{ background: "var(--cw-secondary)", color: "var(--cw-primary-dark)" }}
                  data-testid="notif-badge"
                >
                  {notifCount}
                </span>
              )}
              {item.badgeKey === "approvals" && approvalsCount > 0 && (
                <span
                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                  style={{ background: "var(--cw-secondary)", color: "var(--cw-primary-dark)" }}
                  data-testid="approvals-badge"
                >
                  {approvalsCount}
                </span>
              )}
              {!item.active && (
                <span
                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                  style={{ background: "var(--cw-secondary)", color: "var(--cw-primary-dark)" }}
                >
                  Soon
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <div className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>Signed in as</div>
          <div className="text-sm font-medium truncate" data-testid="current-user-name">
            {user?.full_name}
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: "rgba(255,255,255,0.55)" }}>
            {userTitle(user)}
          </div>
          {!isSuper && (
            <NavLink
              to="/profile"
              className="mt-2 w-full flex items-center gap-2 text-xs py-1.5 px-2 rounded-md hover:bg-white/10 transition"
              data-testid="nav-profile"
            >
              <UserCircle size={12} /> My Profile
            </NavLink>
          )}
          <button
            onClick={() => setShowPwd(true)}
            className="mt-1 w-full flex items-center gap-2 text-xs py-1.5 px-2 rounded-md hover:bg-white/10 transition"
            data-testid="change-password-btn"
          >
            <KeyRound size={12} /> Change password
          </button>
          {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
          <button
            onClick={logout}
            className="mt-1 w-full flex items-center justify-center gap-2 text-xs py-2 rounded-md hover:bg-white/10 transition"
            data-testid="logout-btn"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0" style={{ background: "var(--cw-page)" }}>{children}</main>
    </div>
  );
}
